import React, { useState } from 'react';
import {
	Alert,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	Link,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Contract } from '../../types/contract';
import i18n from '../../i18n';
import { useContracts } from '../../hooks/useContracts';
import { useAppNavigation } from '../../context/NavigationContext';

// Must match the 'Human Resources/Job positions' key in src/config/componentMap.ts
const JOBS_PAGE_SEGMENT = 'Human Resources/Job positions';

export interface ContractValidationErrors {
	startDate?: string;
	endDate?: string;
	jobPosition?: string;
	payrollCalcType?: string;
	withContribution?: string;
	salaryBasis?: string;
	baseAmount?: string;
	workload?: string;
	employerInsuranceBenefit?: string;
	familyInsuranceDeduction?: string;
}

export function validateContractForm(contract: Contract): ContractValidationErrors {
	const errors: ContractValidationErrors = {};
	if (!(contract.startDate || '').trim()) errors.startDate = i18n.t('employee.contract.validation.startDateRequired');
	if ((contract.endDate || '').trim() && (contract.startDate || '').trim()) {
		if (new Date(contract.endDate) <= new Date(contract.startDate)) {
			errors.endDate = i18n.t('employee.contract.validation.endDateAfterStart');
		}
	}
	if (!(contract.jobPosition || '').trim()) errors.jobPosition = i18n.t('employee.contract.validation.jobPositionRequired');
	if (!(contract.payrollCalcType || '').trim()) errors.payrollCalcType = i18n.t('employee.contract.validation.payrollCalcTypeRequired');
	if (!(contract.withContribution || '').trim()) errors.withContribution = i18n.t('employee.contract.validation.withContributionRequired');
	if (!(contract.salaryBasis || '').trim()) errors.salaryBasis = i18n.t('employee.contract.validation.salaryBasisRequired');
	if (!(contract.baseAmount || '').trim()) errors.baseAmount = i18n.t('employee.contract.validation.baseAmountRequired');
	if (!(contract.workload || '').trim()) errors.workload = i18n.t('employee.contract.validation.workloadRequired');
	if (!(contract.employerInsuranceBenefit || '').trim()) errors.employerInsuranceBenefit = i18n.t('employee.contract.validation.employerInsuranceBenefitRequired');
	if (!(contract.familyInsuranceDeduction || '').trim()) errors.familyInsuranceDeduction = i18n.t('employee.contract.validation.familyInsuranceDeductionRequired');
	return errors;
}

export function isContractFormValid(contract: Contract): boolean {
	return Object.keys(validateContractForm(contract)).length === 0;
}

interface NewContractFormComponentProps {
	newContract: Contract;
	branchName?: string;
	handleContractFormChange: (field: keyof Contract) => (event: any) => void;
	showAllErrors?: boolean;
	backendError?: { message: string; violations: string[] } | null;
}

const NewContractFormComponent: React.FC<NewContractFormComponentProps> = ({
	newContract,
	branchName,
	handleContractFormChange,
	showAllErrors = false,
	backendError,
}) => {
	const { t } = useTranslation();
	const [hasEndDate, setHasEndDate] = useState<boolean>(!!newContract.endDate);

	const {
		approvedBranchJobPositions,
		approvedBranchJobPositionsLoading,
		salaryBasisOptions,
		salaryBasisLoading,
		calcTypeOptions,
		calcTypeLoading,
		handleBlur,
		isTouched,
		errors,
		handleSwitchChange,
	} = useContracts(newContract, branchName, handleContractFormChange, showAllErrors);

	const { navigate } = useAppNavigation();

	const handleAddJobClick = (event: React.MouseEvent) => {
		event.preventDefault();
		navigate(JOBS_PAGE_SEGMENT);
	};

	return (
		<>
			<Typography variant="subtitle2" color="primary" sx={{ mb: 0 }}>{t('employee.contract.section.contractInfo')}</Typography>

			{backendError && (
				<Alert severity="error" sx={{ mt: 1 }}>
					<Typography variant="body2" fontWeight="bold">{backendError.message}</Typography>
					{backendError.violations.length > 0 && (
						<ul style={{ margin: '4px 0 0', paddingLeft: '1.25rem' }}>
							{backendError.violations.map((v, i) => (
								<li key={i}>{v}</li>
							))}
						</ul>
					)}
				</Alert>
			)}

			{newContract.contractNumber && (
				<TextField
					label={t('employee.contract.field.contractNumber')}
					value={newContract.contractNumber}
					fullWidth
					disabled
				/>
			)}
			<TextField
				label={t('employee.contract.field.startDate')}
				type="date"
				value={newContract.startDate}
				onChange={handleContractFormChange('startDate')}
				onBlur={handleBlur('startDate')}
				fullWidth
				required
				error={isTouched('startDate') && !!errors.startDate}
				helperText={isTouched('startDate') && errors.startDate}
				InputLabelProps={{
					shrink: true,
				}}
			/>
			<FormControlLabel
				control={
					<Switch
						checked={hasEndDate}
						onChange={(e) => setHasEndDate(e.target.checked)}
					/>
				}
				label={t('employee.contract.hasEndDate')}
			/>
			{hasEndDate && (
				<TextField
					label={t('employee.contract.field.endDate')}
					type="date"
					value={newContract.endDate}
					onChange={handleContractFormChange('endDate')}
					onBlur={handleBlur('endDate')}
					fullWidth
					error={isTouched('endDate') && !!errors.endDate}
					helperText={isTouched('endDate') && errors.endDate}
					InputLabelProps={{
						shrink: true,
					}}
				/>
			)}
			<Divider sx={{ my: 1 }} />
			<Typography variant="subtitle2" color="primary" sx={{ mb: 0 }}>{t('employee.contract.section.jobPayroll')}</Typography>
			<FormControl fullWidth required error={isTouched('jobPosition') && !!errors.jobPosition}>
				<InputLabel>{t('employee.contract.field.jobPosition')}</InputLabel>
				<Select
					value={newContract.jobPosition}
					onChange={handleContractFormChange('jobPosition')}
					onBlur={handleBlur('jobPosition')}
					disabled={approvedBranchJobPositionsLoading}
					label={t('employee.contract.field.jobPosition')}>
					{approvedBranchJobPositionsLoading ? (
						<MenuItem disabled value="">{t('common.loading')}</MenuItem>
					) : approvedBranchJobPositions.length === 0 ? (
						<MenuItem disabled value="">{t('employee.contract.noJobPositions')}</MenuItem>
					) : (
						approvedBranchJobPositions.map((job) => (
							<MenuItem key={job.id} value={job.name}>{job.name}</MenuItem>
						))
					)}
				</Select>
				{isTouched('jobPosition') && errors.jobPosition && (
					<FormHelperText>{errors.jobPosition}</FormHelperText>
				)}
			</FormControl>
			<Link
				component="button"
				type="button"
				variant="body2"
				onClick={handleAddJobClick}
				underline="hover"
				sx={{ alignSelf: 'flex-start' }}
			>
				{t('employee.contract.addJobLink')}
			</Link>
			<FormControl fullWidth required error={isTouched('payrollCalcType') && !!errors.payrollCalcType}>
				<InputLabel>{t('employee.contract.field.payrollCalcType')}</InputLabel>
				<Select
					value={newContract.payrollCalcType}
					onChange={handleContractFormChange('payrollCalcType')}
					onBlur={handleBlur('payrollCalcType')}
					disabled={calcTypeLoading}
					label={t('employee.contract.field.payrollCalcType')}>
					{calcTypeLoading ? (
						<MenuItem disabled value="">{t('common.loading')}</MenuItem>
					) : calcTypeOptions.length === 0 ? (
						<MenuItem disabled value="">{t('employee.contract.noOptionsAvailable')}</MenuItem>
					) : (
						calcTypeOptions.map((option) => (
							<MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
						))
					)}
				</Select>
				{isTouched('payrollCalcType') && errors.payrollCalcType && (
					<FormHelperText>{errors.payrollCalcType}</FormHelperText>
				)}
			</FormControl>
			<FormControl fullWidth required error={isTouched('withContribution') && !!errors.withContribution}>
				<InputLabel>{t('employee.contract.field.withContribution')}</InputLabel>
				<Select value={newContract.withContribution} onChange={handleContractFormChange('withContribution')}
					onBlur={handleBlur('withContribution')} label={t('employee.contract.field.withContribution')}>
					<MenuItem value="1">{t('common.actions.yes')}</MenuItem>
					<MenuItem value="0">{t('common.actions.no')}</MenuItem>
				</Select>
				{isTouched('withContribution') && errors.withContribution && (
					<FormHelperText>{errors.withContribution}</FormHelperText>
				)}
			</FormControl>
			<FormControl fullWidth required error={isTouched('salaryBasis') && !!errors.salaryBasis}>
				<InputLabel>{t('employee.contract.field.salaryBasis')}</InputLabel>
				<Select
					value={newContract.salaryBasis}
					onChange={handleContractFormChange('salaryBasis')}
					onBlur={handleBlur('salaryBasis')}
					disabled={salaryBasisLoading}
					label={t('employee.contract.field.salaryBasis')}>
					{salaryBasisLoading ? (
						<MenuItem disabled value="">{t('common.loading')}</MenuItem>
					) : salaryBasisOptions.length === 0 ? (
						<MenuItem disabled value="">{t('employee.contract.noOptionsAvailable')}</MenuItem>
					) : (
						salaryBasisOptions.map((option) => (
							<MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
						))
					)}
				</Select>
				{isTouched('salaryBasis') && errors.salaryBasis && (
					<FormHelperText>{errors.salaryBasis}</FormHelperText>
				)}
			</FormControl>
			<Divider sx={{ my: 1 }} />
			<Typography variant="subtitle2" color="primary" sx={{ mb: 0 }}>{t('employee.contract.section.compensation')}</Typography>
			<TextField
				label={t('employee.contract.field.baseAmount')}
				type="number"
				value={newContract.baseAmount}
				onChange={handleContractFormChange('baseAmount')}
				onBlur={handleBlur('baseAmount')}
				fullWidth
				required
				error={isTouched('baseAmount') && !!errors.baseAmount}
				helperText={isTouched('baseAmount') && errors.baseAmount}
			/>
			<TextField
				label={t('employee.contract.field.workload')}
				type="number"
				value={newContract.workload}
				onChange={handleContractFormChange('workload')}
				onBlur={handleBlur('workload')}
				fullWidth
				required
				error={isTouched('workload') && !!errors.workload}
				helperText={isTouched('workload') && errors.workload}
			/>
			<TextField
				label={t('employee.contract.field.employerInsuranceBenefit')}
				type="number"
				value={newContract.employerInsuranceBenefit}
				onChange={handleContractFormChange('employerInsuranceBenefit')}
				onBlur={handleBlur('employerInsuranceBenefit')}
				fullWidth
				required
				error={isTouched('employerInsuranceBenefit') && !!errors.employerInsuranceBenefit}
				helperText={isTouched('employerInsuranceBenefit') && errors.employerInsuranceBenefit}
			/>
			<TextField
				label={t('employee.contract.field.familyInsuranceDeduction')}
				type="number"
				value={newContract.familyInsuranceDeduction}
				onChange={handleContractFormChange('familyInsuranceDeduction')}
				onBlur={handleBlur('familyInsuranceDeduction')}
				fullWidth
				required
				error={isTouched('familyInsuranceDeduction') && !!errors.familyInsuranceDeduction}
				helperText={isTouched('familyInsuranceDeduction') && errors.familyInsuranceDeduction}
			/>
			<Divider sx={{ my: 1 }} />
			<Typography variant="subtitle2" color="primary" sx={{ mb: 0 }}>{t('employee.contract.section.additionalOptions')}</Typography>
			<FormControlLabel
				control={
					<Switch
						checked={newContract.fundEnabled === '1'}
						onChange={handleSwitchChange('fundEnabled')}
					/>
				}
				label={t('employee.contract.fundEnabled')}
			/>
			<TextField
				label={t('employee.contract.field.notes')}
				value={newContract.notes}
				onChange={handleContractFormChange('notes')}
				fullWidth
				multiline
				minRows={5}
				maxRows={10}
			/>
		</>
	);
};

export default NewContractFormComponent;